/**
 * What was decided about a message, as system state.
 *
 * Reply Later, Set Aside and Bubble Up are answers about the mail, not fields
 * of it. Held only in this browser's index, they die on a re-index and never
 * reach a second device, so the list lives in an addressable event encrypted
 * to this account alone — the same shape `postr/deleted` uses, for the same
 * reason: nothing server-side reads it, and the relay holds only ciphertext.
 *
 * One event carries the whole map. Replaceable means the newest wins, so two
 * browsers racing to publish converge on whichever wrote last, which is the
 * same settlement delete already accepts.
 */
import type { Event as NostrEvent } from "nostr-tools";
import { gzipSync, gunzipSync } from "fflate";
import type { RelayConnection } from "./relay";
import type { MailSigner } from "./signer";
import { KIND_APP_DATA, decodeBody, toBase64 } from "./contacts";

export const IDENTIFIER = "postr/workflow";

/** What one message has been decided into. Absent fields are settled defaults. */
export interface WorkflowEntry {
  /** In the Follow Up stack, waiting on an answer. */
  later?: boolean;
  /** Filed out of the way, still held, listed nowhere. */
  aside?: boolean;
  /** Epoch seconds after which the message resurfaces at the top. */
  bubbleTo?: number;
}

export interface WorkflowMap {
  version: number;
  updated: string;
  /** By wrap id. Clean messages hold no entry, so the map stays small. */
  flags: Record<string, WorkflowEntry>;
}

export interface WorkflowRecord {
  map: WorkflowMap;
  /** When the event was written, so the next read can skip an unchanged map. */
  createdAt: number;
}

/** Whether an entry still says anything. An entry that does not goes away. */
export function entryIsClean(entry: WorkflowEntry | undefined): boolean {
  return !entry?.later && !entry?.aside && !(entry?.bubbleTo ?? 0);
}

/**
 * Read the map, and only when it has changed.
 *
 * `newerThan` is the `createdAt` of the last read. A decrypt is a round-trip to
 * the phone for a remote signer, and the poller runs every minute, so reading a
 * map that nobody has rewritten costs an approval for nothing.
 */
export async function fetchWorkflow(
  relay: RelayConnection,
  ownerPubkey: string,
  signer: MailSigner,
  newerThan = 0,
): Promise<WorkflowRecord | null> {
  const events = await relay.list({
    kinds: [KIND_APP_DATA],
    authors: [ownerPubkey],
    "#d": [IDENTIFIER],
    limit: 1,
  });
  const newest = events.sort((a, b) => b.created_at - a.created_at)[0];
  if (!newest || newest.created_at <= newerThan) return null;

  const plaintext = await signer.nip44Decrypt(ownerPubkey, newest.content);
  const json = new TextDecoder().decode(gunzipSync(decodeBody(plaintext)));
  return { map: JSON.parse(json) as WorkflowMap, createdAt: newest.created_at };
}

/**
 * Publish a new version.
 *
 * An id whose entry has gone clean drops out of the map entirely, which is what
 * keeps the event small enough to stay readable years into the archive.
 *
 * `after` is the `created_at` of the last map this client saw or sent. The
 * stamp never goes backwards, so two decisions made inside one second cannot
 * tie — a relay resolving a tie picks by id, which is a coin toss about which
 * decision survives everywhere.
 */
export async function publishWorkflow(
  relay: RelayConnection,
  signer: MailSigner,
  flags: Readonly<Record<string, WorkflowEntry>>,
  version: number,
  after = 0,
): Promise<NostrEvent> {
  const kept: Record<string, WorkflowEntry> = {};
  for (const [id, entry] of Object.entries(flags)) {
    if (entryIsClean(entry)) continue;
    kept[id.trim().toLowerCase()] = { ...entry };
  }
  const map: WorkflowMap = {
    version,
    updated: new Date().toISOString(),
    flags: Object.fromEntries(Object.entries(kept).sort(([a], [b]) => a.localeCompare(b))),
  };
  const squeezed = gzipSync(new TextEncoder().encode(JSON.stringify(map)));
  const content = await signer.nip44Encrypt(signer.pubkey, toBase64(squeezed));

  const event = await signer.signEvent({
    kind: KIND_APP_DATA,
    created_at: Math.max(Math.floor(Date.now() / 1000), after + 1),
    tags: [["d", IDENTIFIER]],
    content,
  });
  await relay.publish(event);
  return event;
}
